// -----------------------------------------------------------------------------
// core/serialize.ts — the save/load format.
//
// A ZeroFrame project file is just JSON: the project tree plus the media-asset
// metadata (the raw media bytes live in scratch storage, referenced by
// `scratchKey`, not inlined here). Because the model is plain data, serializing
// is essentially JSON.stringify with a version stamp so future tiers can migrate
// old files. (CLAUDE.md §4: "JSON in/out = the save format".)
// -----------------------------------------------------------------------------
import type { MediaAsset, Project } from './model';

export const FORMAT_VERSION = 1;

export interface SavedDocument {
  zeroframe: { version: number };
  project: Project;
  assets: MediaAsset[];
}

export function serializeProject(project: Project, assets: MediaAsset[]): string {
  const doc: SavedDocument = {
    zeroframe: { version: FORMAT_VERSION },
    project,
    assets,
  };
  return JSON.stringify(doc, null, 2);
}

export function deserializeProject(json: string): SavedDocument {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new Error('This file is not valid JSON — it may be corrupted or not a ZeroFrame project.');
  }
  if (!isSavedDocument(parsed)) {
    throw new Error('This file is not a recognizable ZeroFrame project.');
  }
  if (parsed.zeroframe.version > FORMAT_VERSION) {
    throw new Error(
      `This project was saved by a newer version of ZeroFrame (format v${parsed.zeroframe.version}).`,
    );
  }
  // Domain validation: positive dimensions/fps. A height of 0 would turn the
  // text-scale math into Infinity downstream — fail loudly here instead (§2).
  const p = parsed.project;
  if (!(p.width > 0) || !(p.height > 0) || !(p.fps > 0)) {
    throw new Error('This project has invalid dimensions — width, height and fps must be positive.');
  }
  return parsed;
}

// Structural guard — enough to fail loudly on the obvious "wrong file" cases
// (CLAUDE.md §2: never fail silently).
function isSavedDocument(value: unknown): value is SavedDocument {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  const zf = v.zeroframe as Record<string, unknown> | undefined;
  return (
    !!zf &&
    typeof zf.version === 'number' &&
    typeof v.project === 'object' &&
    v.project !== null &&
    Array.isArray((v.project as Project).tracks) &&
    Array.isArray(v.assets)
  );
}
